import React, { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { LogOut, Menu, ShieldCheck, Users, UserCog, X } from 'lucide-react';
import { useAuth } from './AuthContext';

const AdminLayout: React.FC = () => {
  const { admin, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItemClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
      isActive ? 'bg-accent-amber text-bg-dark' : 'text-neutral-400 hover:bg-white/5 hover:text-white'
    }`;

  const sidebarContent = (
    <>
      <div className="flex items-center justify-between mb-8 px-1">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-accent-amber flex items-center justify-center">
            <ShieldCheck size={16} className="text-bg-dark" />
          </div>
          <span className="font-figtree font-black text-white text-sm">Azura Admin</span>
        </div>
        <button onClick={() => setMobileOpen(false)} className="lg:hidden text-neutral-500 hover:text-white" aria-label="Close menu">
          <X size={20} />
        </button>
      </div>

      <nav className="flex-1 space-y-1.5">
        <NavLink to="/proadmin/users" className={navItemClass} onClick={() => setMobileOpen(false)}>
          <Users size={16} /> Users
        </NavLink>
        {admin?.role === 'super_admin' && (
          <NavLink to="/proadmin/admins" className={navItemClass} onClick={() => setMobileOpen(false)}>
            <UserCog size={16} /> Admins
          </NavLink>
        )}
      </nav>

      <div className="border-t border-white/10 pt-4 mt-4">
        <p className="text-white text-sm font-bold truncate">{admin?.name}</p>
        <p className="text-neutral-500 text-xs truncate mb-3">{admin?.email}</p>
        <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-accent-amber bg-accent-amber/10 border border-accent-amber/20 px-2 py-1 rounded-full mb-3">
          {admin?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
        </span>
        <button onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-wider text-neutral-400 hover:bg-white/5 hover:text-white transition-colors">
          <LogOut size={14} /> Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-neutral-950 lg:flex">
      {/* Mobile top bar */}
      <div className="lg:hidden flex items-center justify-between px-5 py-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-accent-amber flex items-center justify-center">
            <ShieldCheck size={16} className="text-bg-dark" />
          </div>
          <span className="font-figtree font-black text-white text-sm">Azura Admin</span>
        </div>
        <button onClick={() => setMobileOpen(true)} className="text-neutral-400 hover:text-white" aria-label="Open menu">
          <Menu size={22} />
        </button>
      </div>

      {/* Backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-neutral-950 border-r border-white/10 flex flex-col p-5 transition-transform duration-200 lg:static lg:z-auto lg:w-60 lg:shrink-0 lg:translate-x-0 ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {sidebarContent}
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
